import './AudioPlayer.scss';
import 'react-h5-audio-player/lib/styles.css';

import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import SkipNext from '@mui/icons-material/SkipNext';
import SkipPrevious from '@mui/icons-material/SkipPrevious';
import React, { useEffect, useRef, useState } from 'react';
import H5AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import { Modal, ModalBody } from 'reactstrap';

import CircularLoading from '../../Common/CircularLoading/CircularLoading';

const INTRO_AUDIO_IDX = -1;
const DEFAULT_TIME_BETWEEN_AUDIOS = 2500;

const mapNextQuestionIdToIdx = (playlist, nextQuestionId, status) => {
  if (status === 'PENDING' || status === 'COMPLETED') {
    return INTRO_AUDIO_IDX;
  }
  const idx = playlist.audios.findIndex(
    (audio) => audio.questionId === nextQuestionId,
  );

  if (idx === -1) {
    return INTRO_AUDIO_IDX;
  }
  return idx;
};

const mapIdxToNextQuestionId = (playlist, idx) =>
  playlist.audios[idx].questionId;
const mapIdxToAudio = (playlist, idx) => {
  if (idx === INTRO_AUDIO_IDX) {
    return { link: playlist.introAudio };
  }
  return playlist.audios[idx];
};

/*
  "playlistIterator" expects an "iterator" object with the following structure:
  {
    current: attribute with the playlist fetched from the API and stored in Redux.
    getNext(): dispatches action to get the next chapter's playlist. Overwrites the current playlist.
    getPrevious(): dispatches action to get the previous chapter's playlist. Overwrites the current playlist. 
    isFirst(): checks if the current playlist is the first one.
    isLast(): checks if the current playlist is the last one.
  }
  Reference: https://refactoring.guru/design-patterns/iterator
*/
export default function AudioPlayer({
  playlistIterator: iterator,
  open,
  close,
  audioPlaylistsProgress,
  updateAudioPlaylistProgress,
}) {
  const audioPlayerRef = useRef();

  const userProgress = iterator?.current?.chapter
    ? audioPlaylistsProgress[iterator.current.chapter] || {}
    : {};

  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentAudioIdx, setCurrentAudioIdx] = useState(INTRO_AUDIO_IDX);
  const [audioSummary, setAudioSummary] = useState(null);
  const [onlyAudioSummary, setOnlyAudioSummary] = useState(false);

  useEffect(() => {
    if (iterator?.current?.onlyAudioSummary) {
      setOnlyAudioSummary(true);
      setCurrentAudioIdx(0); // Use 0 for single audio summary
      setCurrentAudio({ link: iterator.current.audioSummary });
    } else if (iterator?.current?.audios?.length > 0) {
      setOnlyAudioSummary(false);
      setAudioSummary(iterator.current.audioSummary);
      const newIdx = mapNextQuestionIdToIdx(
        iterator.current,
        userProgress.nextQuestionId,
        userProgress.status,
      );
      setCurrentAudioIdx(newIdx);
      const audio = mapIdxToAudio(iterator?.current, newIdx);
      setCurrentAudio(audio);
    }
  }, [iterator?.current?.id, userProgress.status, iterator?.current?.chapter]);

  useEffect(() => {
    // only execute if the modal is open, otherwise do nothing
    if (!open) return;

    // Pause any orphaned audio elements from previous player instances,
    // but not the one thats about to autoPlay
    const currentAudioElements =
      audioPlayerRef.current?.querySelectorAll('audio');
    const currentAudioSet = new Set(currentAudioElements || []);

    // timeout 0 forces to wait until previous audio has finished rendering and unmounting
    setTimeout(() => {
      const allAudios = document.querySelectorAll('audio');
      allAudios.forEach((a) => {
        if (!currentAudioSet.has(a)) {
          a.pause();
          a.currentTime = 0;
        }
      });
    }, 0);
  }, [open]);

  const isFirst = (audioIdx = currentAudioIdx) => {
    if (onlyAudioSummary) return true;
    return audioIdx === INTRO_AUDIO_IDX;
  };

  const isLast = (audioIdx = currentAudioIdx) => {
    if (onlyAudioSummary) return true;
    return iterator?.current?.audios
      ? audioIdx === iterator.current.audios.length - 1
      : true;
  };

  const handlePrevious = () => {
    const newIdx = isFirst() ? INTRO_AUDIO_IDX : currentAudioIdx - 1;
    const audio = mapIdxToAudio(iterator.current, newIdx);
    setCurrentAudioIdx(newIdx);
    setCurrentAudio(audio);
  };

  const handleNext = () => {
    const newIdx = isLast() ? currentAudioIdx : currentAudioIdx + 1;
    const audio = mapIdxToAudio(iterator.current, newIdx);
    setCurrentAudioIdx(newIdx);
    setCurrentAudio(audio);
    const accumulated = audioSummary
      ? userProgress.accumulated + 1
      : userProgress.accumulated;
    if (newIdx >= accumulated) {
      updateAudioPlaylistProgress(
        iterator.current.chapter,
        mapIdxToNextQuestionId(iterator.current, newIdx),
        'STARTED',
      );
    }
  };

  const handleOnEnd = () => {
    setTimeout(
      () => {
        if (isLast()) {
          if (!onlyAudioSummary) {
            updateAudioPlaylistProgress(
              iterator.current.chapter,
              INTRO_AUDIO_IDX,
              'COMPLETED',
            );
          }
          if (!iterator.isLast()) {
            iterator.getNext();
          } else {
            close();
          }
        } else {
          handleNext();
        }
      },
      iterator.current.timeBetweenQuestions * 1000 ??
        DEFAULT_TIME_BETWEEN_AUDIOS,
    );
  };

  const handleClickOutside = (event) => {
    if (
      audioPlayerRef.current &&
      !audioPlayerRef.current.contains(event.target)
    ) {
      close();
    }
  };

  const customIcons = {
    previous: (
      <NavigateBefore
        disabled={isFirst()}
        className={
          isFirst()
            ? 'navigate-icon disabled previous-question'
            : 'navigate-icon enabled previous-question'
        }
      />
    ),
    next: (
      <NavigateNext
        disabled={isLast()}
        className={
          isLast()
            ? 'navigate-icon disabled next-question'
            : 'navigate-icon enabled next-question'
        }
      />
    ),
  };

  const customAdditionalControls = () => [
    <div className="custom-additional-controls">
      <span className="custom-additional-data">
        {onlyAudioSummary
          ? '1 of 1 audio summary'
          : `${currentAudioIdx + 1} of ${iterator?.current?.audios?.length ?? 0} audio questions`}
      </span>
      {userProgress.status === 'COMPLETED' && (
        <span className="custom-additional-data-completed">
          Already completed
        </span>
      )}
    </div>,
  ];

  const customControlsSection = () => [
    RHAP_UI.ADDITIONAL_CONTROLS,
    <SkipPrevious
      disabled={iterator.isFirst()}
      onClick={iterator.getPrevious}
      className={
        iterator.isFirst()
          ? 'navigate-icon disabled previous-playlist'
          : 'navigate-icon enabled previous-playlist'
      }
    />,
    RHAP_UI.MAIN_CONTROLS,
    <SkipNext
      disabled={iterator.isLast()}
      onClick={iterator.getNext}
      className={
        iterator.isLast()
          ? 'navigate-icon disabled next-playlist'
          : 'navigate-icon enabled next-playlist'
      }
    />,
    RHAP_UI.VOLUME_CONTROLS,
  ];

  return (
    <Modal isOpen={open} className="audio-modal" onClick={handleClickOutside}>
      <ModalBody>
        {!currentAudio && <CircularLoading />}
        {currentAudio && iterator && (
          <div className="audio-player-container" ref={audioPlayerRef}>
            <H5AudioPlayer
              autoPlay // autoplays the first audio
              src={`${currentAudio.link}?t=${Date.now()}`}
              showSkipControls
              showJumpControls={false}
              autoPlayAfterSrcChange
              className="custom-audio-player"
              header={
                <div className="chapter-title">{iterator.current.title}</div>
              }
              customIcons={customIcons}
              customAdditionalControls={customAdditionalControls()}
              customControlsSection={customControlsSection()}
              onClickPrevious={handlePrevious}
              onClickNext={handleNext}
              onEnded={handleOnEnd}
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}
